/**
 * GA4 Event Tracking for Vybe Tribe
 *
 * All events are sent to GA4 measurement ID: G-RKGBZ232LG
 * This is shared between the landing page and the app.
 */

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

// Helper to ensure gtag is available
const gtag = (...args: any[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args as any);
  } else {
    console.warn('[GA4] gtag not available:', args);
  }
};

// Core tracking function
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  gtag('event', eventName, params);
  console.debug('[GA4]', eventName, params);
};

// ============================================================================
// USER EVENTS
// ============================================================================

export const trackUserSignUp = (params: {
  user_id: string;
  method: 'email' | 'google' | 'apple';
  subscription_tier: 'free' | 'subscribed' | 'lapsed';
}) => {
  trackEvent('user_sign_up', params);
};

// ============================================================================
// VYBE EVENTS
// ============================================================================

export const trackVybeCreated = (params: {
  vybe_id: string;
  user_id: string;
  category: string;
  location_area: string;
  is_free: boolean;
}) => {
  trackEvent('vybe_created', params);
};

export const trackVybeApplicationSent = (params: {
  vybe_id: string;
  user_id: string;
  category: string;
  host_id: string;
  apply_tokens_remaining: number;
}) => {
  trackEvent('vybe_application_sent', params);
};

export const trackVybeApplicationAccepted = (params: {
  vybe_id: string;
  user_id: string;
  host_id: string;
  category: string;
}) => {
  trackEvent('vybe_application_accepted', params);
};

// ============================================================================
// UPGRADE FUNNEL EVENTS
// ============================================================================

export const trackUpgradeClicked = (params: {
  user_id: string;
  current_tier: 'free' | 'subscribed' | 'lapsed';
  entry_point: 'token_hub' | 'limit_block' | 'profile' | 'pricing_page' | string;
}) => {
  trackEvent('upgrade_clicked', params);
};

export const trackUpgradeSuccess = (params: {
  user_id: string;
  new_tier: 'subscribed';
  amount: number;
  currency: string;
  plan_id?: string;
}) => {
  trackEvent('upgrade_success', params);
};

// ============================================================================
// SIGN-UP FLOW TRACKING
// ============================================================================

export const trackSignupStepStarted = (params: {
  user_id?: string;
  step_name: string;
  step_number: number;
}) => {
  trackEvent(`signup_step_${params.step_number}_started`, {
    user_id: params.user_id,
    step_name: params.step_name,
  });
};

export const trackSignupStepCompleted = (params: {
  user_id?: string;
  step_name: string;
  step_number: number;
}) => {
  trackEvent(`signup_step_${params.step_number}_completed`, {
    user_id: params.user_id,
    step_name: params.step_name,
  });
};

export const trackSignupCompleted = (params: {
  user_id: string;
  method: 'email' | 'google' | 'apple';
}) => {
  trackEvent('signup_completed', params);
};

// ============================================================================
// CREATE VYBE FLOW TRACKING
// ============================================================================

export const trackCreateVybeStepStarted = (params: {
  user_id: string;
  step_name: string;
  step_number: number;
  category?: string;
}) => {
  trackEvent(`create_vybe_step_${params.step_number}_started`, {
    user_id: params.user_id,
    step_name: params.step_name,
    category: params.category,
  });
};

export const trackCreateVybeStepCompleted = (params: {
  user_id: string;
  step_name: string;
  step_number: number;
  category?: string;
}) => {
  trackEvent(`create_vybe_step_${params.step_number}_completed`, {
    user_id: params.user_id,
    step_name: params.step_name,
    category: params.category,
  });
};

export const trackCreateVybePublishClicked = (params: {
  user_id: string;
  category: string;
}) => {
  trackEvent('create_vybe_publish_clicked', params);
};

// ============================================================================
// CATEGORY STANDARDIZATION
// ============================================================================

/**
 * Standardizes category strings for consistent tracking
 * Maps user-facing categories to clean, lowercase tracking values
 */
export const standardizeCategory = (category: string): string => {
  const categoryMap: Record<string, string> = {
    'Coffee & Drinks': 'coffee',
    'Coffee': 'coffee',
    'Drinks': 'drinks',
    'Cinema & Movies': 'cinema',
    'Cinema': 'cinema',
    'Movies': 'cinema',
    'Outdoors & Nature': 'outdoors',
    'Outdoors': 'outdoors',
    'Nature': 'outdoors',
    'Games & Gaming': 'games',
    'Games': 'games',
    'Gaming': 'games',
    'Fitness & Sports': 'fitness',
    'Fitness': 'fitness',
    'Sports': 'fitness',
    'Food & Dining': 'food',
    'Food': 'food',
    'Dining': 'food',
    'Arts & Culture': 'arts',
    'Arts': 'arts',
    'Culture': 'arts',
    'Music & Concerts': 'music',
    'Music': 'music',
    'Concerts': 'music',
    'Nightlife & Parties': 'nightlife',
    'Nightlife': 'nightlife',
    'Parties': 'nightlife',
    'Learning & Education': 'learning',
    'Learning': 'learning',
    'Education': 'learning',
    'Wellness & Mindfulness': 'wellness',
    'Wellness': 'wellness',
    'Mindfulness': 'wellness',
    'Travel & Adventure': 'travel',
    'Travel': 'travel',
    'Adventure': 'travel',
    'Tech & Innovation': 'tech',
    'Tech': 'tech',
    'Innovation': 'tech',
    'Other': 'other',
  };

  return categoryMap[category] || category.toLowerCase().replace(/\s+/g, '_');
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Set user ID for all subsequent events
 * Call this when user logs in
 */
export const setUserId = (userId: string) => {
  gtag('config', 'G-RKGBZ232LG', {
    user_id: userId,
  });
};

/**
 * Clear user ID (e.g., on logout)
 */
export const clearUserId = () => {
  gtag('config', 'G-RKGBZ232LG', {
    user_id: undefined,
  });
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, any>) => {
  gtag('config', 'G-RKGBZ232LG', {
    user_properties: properties,
  });
};

// ============================================================================
// CONVENIENCE WRAPPERS FOR EXISTING ANALYTICS
// ============================================================================

/**
 * Compatibility wrapper for existing analytics.ts
 * Maps existing Events enum to GA4 events
 */
export const trackLegacyEvent = (eventName: string, properties?: Record<string, any>) => {
  // Map old event names to new GA4 event names
  const eventMap: Record<string, string> = {
    'signup_started': 'signup_step_1_started',
    'signup_succeeded': 'user_sign_up',
    'email_verification_complete': 'email_verified',
    'onboarding_completed': 'signup_completed',
    'vybe_created': 'vybe_created',
    'token_consumed': 'token_used',
    'token_declined': 'token_limit_reached',
    'upgrade_clicked': 'upgrade_clicked',
  };

  const mappedEventName = eventMap[eventName] || eventName;
  trackEvent(mappedEventName, properties);
};
